// ============================================================================
// TagFilter — pill row of selectable event tags.
// ============================================================================
// Fetches active tags from /api/tags and renders them as horizontal pills
// grouped by category (topics first, programs second). Multi-select with
// OR-logic: events matching ANY selected tag pass the filter.
//
// State lives in the parent (MainApp) so multiple consumers — e.g. a future
// TagFilter in the calendar grid as well as the bottom list — can share
// selected tags. When selectedTags is empty, no filter is applied.
// ============================================================================

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api, type EventTag } from '../api';

interface TagFilterProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

export function TagFilter({ selectedTags, onChange }: TagFilterProps) {
  const [tags, setTags] = useState<EventTag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .tags()
      .then((data) => {
        setTags(data.tags ?? []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  if (loading || tags.length === 0) return null;

  const toggle = (name: string) => {
    const set = new Set(selectedTags);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    onChange(Array.from(set));
  };

  const isSelected = (name: string) => selectedTags.includes(name);

  const topics = tags.filter((t) => t.tag_category === 'topic');
  const programs = tags.filter((t) => t.tag_category === 'program');

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-gray-500 font-medium mr-1">Filter:</span>

      {topics.map((t) => (
        <button
          key={t.tag_id}
          type="button"
          onClick={() => toggle(t.tag_name)}
          className={
            'rounded-full px-3 py-1 border transition-colors ' +
            (isSelected(t.tag_name)
              ? 'bg-uif-green text-white border-uif-green-dark'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
          }
        >
          {t.tag_name}
        </button>
      ))}

      {programs.length > 0 && topics.length > 0 && (
        <span aria-hidden="true" className="text-gray-300 px-1">|</span>
      )}

      {programs.map((t) => (
        <button
          key={t.tag_id}
          type="button"
          onClick={() => toggle(t.tag_name)}
          className={
            'rounded-full px-3 py-1 border transition-colors font-medium ' +
            (isSelected(t.tag_name)
              ? 'bg-uif-brown text-white border-uif-brown'
              : 'bg-white text-uif-brown-dark border-uif-brown/40 hover:bg-uif-brown/5')
          }
        >
          {t.tag_name}
        </button>
      ))}

      {selectedTags.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="inline-flex items-center gap-1 ml-2 text-gray-500 hover:text-gray-700"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      )}
    </div>
  );
}
